/**
 * SC-001: Вход в админку через Drafter ID (B-1973, SSO).
 *
 * **Это единственная реальная точка входа в панель**: `useLogin`/`<AuthPage>` в
 * приложении нет, `authProvider.login()` не вызывается. Гейт роли правится
 * здесь; предикат общий — `lib/panel-access`.
 *
 * Формы нет. При заходе пытаемся молча обменять общую cookie Драфтера
 * (`exchange-drafter`, audience=admin) на локальный сеанс:
 * - успех + доступ в панель (is_admin ИЛИ is_mentor, B-2175/B-2221) → сохраняем access
 *   и уходим на домашнюю страницу роли: админ — дашборд, ментор — очередь ДЗ;
 * - успех, но ролей нет (или ответ без `user`) → «нет доступа»;
 * - 401 (нет сессии/протухла) → редирект на форму входа `id.drafter.ru`;
 * - 503/сеть → показываем ошибку с повтором.
 */

import { Button, Card, Result, Spin, Typography } from "antd";
import { useEffect, useState } from "react";

import { exchangeViaCookie, redirectToDrafterId } from "../../lib/drafter-auth";
import { hasPanelAccess, homePathFor, panelRoleOf } from "../../lib/panel-access";
import { tokenStorage } from "../../lib/token-storage";

type State = { kind: "loading" } | { kind: "no-access" } | { kind: "error"; message: string };

export const LoginPage = (): JSX.Element => {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { accessToken, user } = await exchangeViaCookie();
        if (cancelled) return;
        if (!hasPanelAccess(user)) {
          setState({ kind: "no-access" });
          return;
        }
        tokenStorage.saveAccessToken(accessToken);
        // Полный переход — Refine перечекает авторизацию с новым токеном.
        window.location.replace(homePathFor(panelRoleOf(user)));
      } catch (error) {
        if (cancelled) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          // Нет валидной сессии Драфтера → на форму входа IdP.
          redirectToDrafterId();
          return;
        }
        setState({
          kind: "error",
          message:
            status === 503
              ? "Сервис авторизации временно недоступен."
              : "Не удалось войти. Попробуйте ещё раз.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f0f2f5",
      }}
    >
      <Card style={{ width: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <img
            src="/logo/mark.svg"
            alt="DRAFTER"
            style={{
              height: 56,
              width: 56,
              background: "#0B1438",
              borderRadius: 12,
              padding: 6,
            }}
          />
          <Typography.Title level={4} style={{ marginTop: 12, marginBottom: 0 }}>
            DRAFTER
          </Typography.Title>
          <Typography.Text type="secondary">Админ-панель</Typography.Text>
        </div>

        {state.kind === "loading" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin size="large" />
            <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
              Входим через Drafter ID…
            </Typography.Paragraph>
          </div>
        )}

        {state.kind === "no-access" && (
          <Result
            status="403"
            subTitle="У этого аккаунта нет доступа к админ-панели."
            extra={
              <Button type="primary" onClick={redirectToDrafterId}>
                Войти другим аккаунтом
              </Button>
            }
          />
        )}

        {state.kind === "error" && (
          <Result
            status="warning"
            subTitle={state.message}
            extra={
              <Button type="primary" onClick={() => window.location.reload()}>
                Повторить
              </Button>
            }
          />
        )}
      </Card>
    </div>
  );
};
